import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";

const usd = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString("en-US")}`);
const inputCls = "h-11 rounded-md border-input bg-white text-[15px]";
const COPY = {
  value: { title: "Instant home value estimate", button: "Get my instant estimate", unit: "" },
  rent: { title: "Instant rent estimate", button: "Estimate my rent", unit: "/mo" },
};

const Result = ({ r, kind }) => (
  <div className="mt-6 animate-rise-in" data-testid={`estimate-${kind}-result`}>
    <p className="text-[13px] text-neutral-600">{r.address}</p>
    <p className="mt-1 font-display text-5xl text-ink" data-testid={`estimate-${kind}-amount`}>{usd(r.estimate)}<span className="text-2xl text-neutral-500">{COPY[kind].unit}</span></p>
    <p className="mt-1 text-[14px] text-neutral-600">Likely range {usd(r.low)} to {usd(r.high)}{COPY[kind].unit}</p>
    {r.comparables?.length > 0 && (
      <div className="mt-5">
        <p className="eyebrow mb-2">Nearby comparables</p>
        <ul className="divide-y divide-neutral-200 text-[14px]" data-testid={`estimate-${kind}-comps`}>
          {r.comparables.map((c, i) => (
            <li key={`${c.address}-${i}`} className="flex flex-wrap justify-between gap-2 py-2">
              <span className="min-w-0 text-neutral-700">{c.address}{c.miles != null && <span className="text-neutral-500"> · {c.miles} mi</span>}</span>
              <span className="font-medium text-ink">{usd(c.price)}{COPY[kind].unit}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
    <p className="mt-4 text-[12px] leading-relaxed text-neutral-500">Automated estimate from public records and nearby listings (RentCast). Not an appraisal; our team's free analysis counts upgrades and condition.</p>
  </div>
);

/** Address -> instant value or rent estimate. Falls back to pointing people at the free request form. */
export const EstimateTool = ({ kind = "value", formAnchor = "guide-form" }) => {
  const [enabled, setEnabled] = useState(null);
  const [a, setA] = useState({ street: "", city: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/estimate-status").then((r) => setEnabled(Boolean(r.data?.enabled))).catch(() => setEnabled(false));
  }, []);
  const set = (k) => (e) => setA((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!a.street.trim() || !a.city.trim() || !/^\d{5}$/.test(a.zip)) return setErr("Please enter the street, city and a 5-digit ZIP.");
    setBusy(true); setErr(""); setRes(null);
    try {
      const r = await api.get(`/estimate/${kind}`, { params: a });
      setRes(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail?.message || errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };
  const goForm = () => document.getElementById(formAnchor)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="rounded-md border border-neutral-200 bg-white p-6 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.35)] sm:p-8" data-testid={`estimate-${kind}-tool`}>
      <p className="flex items-center gap-2 text-lg font-medium text-ink"><Sparkles className="h-5 w-5 text-brass" /> {COPY[kind].title}</p>
      {enabled === false ? (
        <div className="mt-4 rounded-md bg-mist p-4 text-[14px] text-neutral-700" data-testid={`estimate-${kind}-soon`}>
          Instant estimates are switching on soon. Meanwhile our team will run the numbers for you, free.
          <button type="button" onClick={goForm} className="mt-2 block font-medium text-ink underline underline-offset-4" data-testid={`estimate-${kind}-to-form`}>Request my free analysis</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-[1.6fr_1fr_0.7fr]" data-testid={`estimate-${kind}-form`}>
          <Input className={inputCls} placeholder="Street address" value={a.street} onChange={set("street")} autoComplete="address-line1" data-testid={`estimate-${kind}-street`} />
          <Input className={inputCls} placeholder="City" value={a.city} onChange={set("city")} autoComplete="address-level2" data-testid={`estimate-${kind}-city`} />
          <Input className={inputCls} placeholder="ZIP" inputMode="numeric" maxLength={5} value={a.zip} onChange={set("zip")} autoComplete="postal-code" data-testid={`estimate-${kind}-zip`} />
          <button type="submit" disabled={busy || enabled === null} data-testid={`estimate-${kind}-submit`}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-ink px-6 text-[15px] font-medium text-white transition-colors hover:bg-neutral-800 disabled:opacity-60 sm:col-span-3">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {COPY[kind].button}
          </button>
        </form>
      )}
      {err && (
        <p className="mt-4 rounded-md bg-red-50 p-3 text-[14px] text-red-700" data-testid={`estimate-${kind}-error`}>
          {err} <button type="button" onClick={goForm} className="font-medium underline underline-offset-4">Request a free analysis</button>
        </p>
      )}
      {res && <Result r={res} kind={kind} />}
    </div>
  );
};
